import React, { useEffect, useState, useRef, useCallback } from 'react';

import cx from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import Scrollbars from 'react-custom-scrollbars-2';
import Modal from 'react-modal';

import styles from './BiometricDetails.module.scss';
import BiometricDveicesApi from '../../../../api/BiometricDevicesApi';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';
import modalStyles from '../../../../components/modalStyles';
import SpinnerRing from '../../../../components/SpinnerRing';
import Tooltip from '../../../../components/Tooltip';

const addModalStyle = _.cloneDeep(modalStyles);
addModalStyle.content.width = 'min(480px, 94vw)';
addModalStyle.content.marginTop = '100px';

const BiometricDetails = ({ id, tenant, onClose, onEditNote, onAddNote, actionLoading }) => {
  const [details, setDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(undefined);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scope = useRef({ id, tenant });
  if (scope.current.id !== id || scope.current.tenant !== tenant) scope.current = { id, tenant };
  const load = useCallback(async () => {
    const { current } = scope;
    const request = {};
    current.request = request;
    if (id == null) return;
    setLoading(true);
    setLoadError('');
    try {
      const [device, list] = await Promise.all([
        BiometricDveicesApi.getDeviceById(id),
        BiometricDveicesApi.getDeviceNotes(id),
      ]);
      if (!Array.isArray(list.data?.items)) throw new Error('Invalid note list');
      if (scope.current === current && current.request === request) {
        setDetails(device.data);
        setNotes(list.data.items);
        setLoaded(true);
      }
    } catch {
      if (scope.current === current && current.request === request)
        setLoadError('Unable to load device details and notes. Please retry.');
    } finally {
      if (scope.current === current && current.request === request) setLoading(false);
    }
  }, [id]);
  useEffect(() => {
    setDetails({});
    setNotes([]);
    setLoaded(false);
    setSelectedNote(null);
    setShowDeleteModal(false);
    setDeleting(false);
    setActionError('');
    load();
    const { current } = scope;
    return () => {
      if (scope.current === current) scope.current = {};
    };
  }, [load, tenant]);

  const onConfirm = async () => {
    const { current } = scope;
    if (current.deleting || !selectedNote?.canDelete) return;
    current.deleting = true;
    setDeleting(true);
    setActionError('');
    try {
      await BiometricDveicesApi.deleteNote(id, selectedNote.id);
      if (scope.current === current) {
        current.request = null;
        setLoading(false);
        setNotes((rows) => rows.filter((item) => item.id !== selectedNote.id));
        setShowDeleteModal(false);
        setSelectedNote(null);
      }
    } catch {
      if (scope.current === current) setActionError('Unable to delete note. Please retry.');
    } finally {
      if (scope.current === current) {
        current.deleting = false;
        setDeleting(false);
      }
    }
  };

  return (
    <>
      <div className='pt-4 ps-5'>
        {(loading || actionLoading) && <SpinnerRing />}
        {loadError && (
          <div role='alert'>
            {loadError}{' '}
            <button type='button' disabled={loading} onClick={load}>
              Retry
            </button>
          </div>
        )}
        {actionError && (
          <div role='alert'>
            {actionError}{' '}
            <button type='button' disabled={deleting || actionLoading} onClick={onConfirm}>
              Retry delete
            </button>
          </div>
        )}
        <div>
          <Scrollbars
            className='scroll-bar'
            autoHide
            renderView={({ style, ...props }) => <div {...props} style={{ ...style, overflowX: 'hidden' }} />}
            style={{ height: 'min(720px, 65vh)' }}
          >
            <div>
              <div className='pe-5 pt-5'>
                <p className='fw-bold mt-1 mb-3'>Device info</p>
                <div className='row'>
                  <div className={cx(styles.inputHeight, 'form-group col-12 col-sm-6')}>
                    <p className='label-title'>Serial number</p>
                    <div className='mb-3 text-steal'>
                      {details.serialNumber?.length > 25 ? (
                        <div className='tooltip-box'>
                          {details.serialNumber.substring(0, 25)}...
                          <Tooltip label={details.serialNumber} />
                        </div>
                      ) : (
                        details.serialNumber
                      )}
                    </div>
                  </div>
                  <div className={cx(styles.inputHeight, 'form-group col-12 col-sm-6')}>
                    <p htmlFor='workEmail' className='label-title'>
                      Device protocol
                    </p>
                    <p className='mb-3 text-steal'>{details.protocol === 'OTHER' ? 'Other' : details.protocol}</p>
                  </div>
                  <div className={cx(styles.inputHeight, 'form-group col-12 col-sm-6')}>
                    <p className='label-title'>Device Time Zone</p>
                    <p className='mb-3 text-steal'>{details.timeZone}</p>
                  </div>
                  <div className={cx(styles.inputHeight, 'form-group col-12 col-sm-6')}>
                    <p className='label-title'>Added to Unbilled Charges</p>
                    <p className='mb-3 text-steal'>
                      {details.isAddedToUnbilledCharges === true
                        ? 'YES'
                        : details.isAddedToUnbilledCharges === false
                        ? 'NO'
                        : 'Unknown'}
                    </p>
                  </div>
                  <div className={cx(styles.inputHeight, 'form-group col-12 col-sm-6')}>
                    <p className='label-title'>Device token</p>
                    <div className='mb-3 text-steal'>
                      {details.token?.length > 30 ? (
                        <div className='tooltip-box'>
                          {details.token.substring(0, 30)}...
                          <Tooltip label={details.token} />
                        </div>
                      ) : (
                        details.token
                      )}
                    </div>
                  </div>
                  <div className={cx(styles.inputHeight, 'form-group col-12 col-sm-6')}>
                    <p className='label-title'>Last sync</p>
                    <p className='mb-3 text-steal'>
                      {details.lastImportDate ? moment(details.lastImportDate).format('MMM DD, YYYY (HH:mm)') : '- -'}
                    </p>
                  </div>
                </div>
                <div className={styles.line} />
                <p className='fw-bold mt-5 mb-3'>Cost</p>
                <div className='row'>
                  <div className={cx(styles.inputHeight, 'form-group col-12 col-sm-6')}>
                    <p className='label-title'>Licence cost:</p>
                    <p className='mb-3 text-steal'>SAR 240.00 / 1 Year</p>
                  </div>
                  <div className={cx(styles.inputHeight, 'form-group col-12 col-sm-6')}>
                    <p className='label-title'>Integration cost:</p>
                    <p className='mb-3 text-steal'>{details?.protocol === 'CAMS' ? 'SAR 490.00' : 'SAR 790.00'}</p>
                  </div>
                </div>
                <div className={styles.line} />
                <p className='fw-bold mt-5 mb-3'>Configured by</p>
                <div className='row'>
                  <div className={cx(styles.inputHeight, 'form-group col-12 col-sm-6')}>
                    <p className='label-title'>Name</p>
                    <p className='mb-3 text-steal'>{details.configuredBy?.fullName}</p>
                  </div>
                  <div className={cx(styles.inputHeight, 'form-group col-12 col-sm-6')}>
                    <p className='label-title'>Configured at</p>
                    <p className='mb-3 text-steal'>
                      {details.configuredAt ? moment(details.configuredAt).format('MMM DD, YYYY (HH:mm)') : '- -'}
                    </p>
                  </div>
                </div>
                <div className={styles.line} />
                <div className='d-flex align-items-center justify-content-between'>
                  <p className='fw-bold mt-5 mb-3'>Notes</p>
                  <button
                    disabled={loading || deleting || actionLoading}
                    type='button'
                    onClick={() => {
                      onAddNote(id);
                    }}
                    className='btn btn-primary text-white'
                  >
                    <div className='d-flex align-items-center justify-content-between'>
                      <i className='icon-plus me-2' />
                      <span> Add Note</span>
                    </div>
                  </button>
                </div>
                <div className='row'>
                  {notes.length > 0 ? (
                    notes.map((item) => (
                      <div
                        key={item.id}
                        className={cx(
                          styles.inputHeight,
                          styles.notes,
                          'd-flex align-items-start justify-content-between col-12'
                        )}
                      >
                        <div className=''>
                          <p className='label-title'>{`${item.user || 'Unknown author'} (${moment(
                            item.createdAt
                          ).format('MMM DD, YYYY HH:mm')})`}</p>
                          <p className='mb-3 text-steal'>{item.content}</p>
                        </div>
                        {(item.canUpdate === true || item.canDelete === true) && (
                          <div className='d-flex justify-content-end'>
                            {item.canUpdate === true && (
                              <button
                                disabled={deleting || actionLoading}
                                type='button'
                                onClick={() => {
                                  onEditNote(item.id);
                                }}
                                title='Edit'
                                className='btn btn-light ms-1'
                              >
                                <i className='icon-pencil-create' />
                              </button>
                            )}
                            {item.canDelete === true && (
                              <button
                                disabled={deleting || actionLoading}
                                type='button'
                                onClick={() => {
                                  setActionError('');
                                  setSelectedNote(item);
                                  setShowDeleteModal(true);
                                }}
                                title='Delete'
                                className='btn btn-light ms-1'
                              >
                                <i className='icon-trash' />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  ) : loaded && !loadError && !loading ? (
                    <div className={cx(styles.emptyList, 'm-auto')}>
                      <div
                        className={cx(
                          styles.iconHistory,
                          'd-flex',
                          'justify-content-center',
                          'align-items-center',
                          'm-auto'
                        )}
                      >
                        <i className={cx(styles.icon, 'icon-housing')} />
                      </div>
                      <p className='mt-4 text-center text-capitalize'>No notes for this device</p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </Scrollbars>
          <div className='d-flex justify-content-end border-t py-4 mt-1 me-5'>
            <button className='btn btn-primary text-white' onClick={onClose} type='button'>
              Close
            </button>
          </div>
        </div>
      </div>
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Modal
          isOpen={showDeleteModal}
          ariaHideApp={false}
          closeTimeoutMS={300}
          style={addModalStyle}
          onRequestClose={() => {
            if (!deleting) setShowDeleteModal(false);
          }}
        >
          <ConfirmModal
            onClose={() => {
              if (!deleting) setShowDeleteModal(false);
            }}
            onSubmit={onConfirm}
            isSending={deleting}
            error={actionError}
            modalName='Remove Note'
            confirmName='Delete'
            confirmText='Are you sure you want to remove this note?'
          />
        </Modal>
      </div>
    </>
  );
};

export default BiometricDetails;
